// Signing in to the patient portal (docs/plans/phase3-portal-feedback-kiswahili.md §4.1, §7.1):
// the mobile number, then the six-digit code from the SMS. The challenge token stays in
// memory only, never in the address or storage. The answer to "send code" is the same
// whether or not the number is registered, so the page says "if this number is registered".
import { normalisePhone } from '@wodi/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Button, LoadingBlock, Notice, TextField } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { portalInfoQuery, portalKeys, requestPortalCode, verifyPortalCode } from '../api/portal.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, useI18n } from '../i18n/i18n.tsx';
import { PortalFrame } from './frame.tsx';
import styles from './portal.module.css';

const route = getRouteApi('/portal/$code');

interface Challenge {
  token: string;
  resendAt: number;
}

function useSecondsUntil(at: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (at === null) return undefined;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [at]);
  return at === null ? 0 : Math.max(0, Math.ceil((at - now) / 1000));
}

export function PortalSignInPage() {
  const { code } = route.useParams();
  const { reason } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnline();
  const { t, language } = useI18n();
  const info = useQuery(portalInfoQuery(code));
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const wait = useSecondsUntil(challenge?.resendAt ?? null);

  const ask = useMutation({
    mutationFn: (msisdn: string) =>
      requestPortalCode({ portalCode: code, phone: msisdn, language }),
    onSuccess: (result) => {
      setChallenge({
        token: result.challengeToken,
        resendAt: Date.now() + result.resendAfterSeconds * 1000,
      });
      setOtp('');
    },
  });

  const verify = useMutation({
    mutationFn: (value: string) =>
      verifyPortalCode({ challengeToken: challenge?.token ?? '', code: value }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portalKeys.me });
      await navigate({ to: '/portal/$code/home', params: { code } });
    },
    onError: () => {
      setOtp('');
    },
  });

  const sendCode = () => {
    const normalised = normalisePhone(phone);
    if (!normalised.ok) {
      setPhoneError(t('portal.signIn.phoneInvalid'));
      return;
    }
    setPhoneError(null);
    ask.mutate(normalised.value);
  };

  const signIn = () => {
    if (!/^\d{6}$/.test(otp.trim())) {
      setOtpError(t('portal.signIn.codeInvalid'));
      return;
    }
    setOtpError(null);
    verify.mutate(otp.trim());
  };

  if (info.isPending) {
    return (
      <PortalFrame organisationName={t('portal.title')}>
        <LoadingBlock label={t('portal.loadingFacility')} rows={3} />
      </PortalFrame>
    );
  }
  if (info.isError) {
    const unknown = info.error instanceof ApiError && info.error.status === 404;
    return (
      <PortalFrame organisationName={t('portal.title')}>
        <Notice
          tone="error"
          {...(unknown
            ? {}
            : {
                action: (
                  <Button dense isDisabled={!online} onPress={() => void info.refetch()}>
                    {t('common.tryAgain')}
                  </Button>
                ),
              })}
        >
          {unknown ? t('portal.unknownAddress') : errorText(t, language, info.error)}
        </Notice>
      </PortalFrame>
    );
  }

  const error = ask.error ?? verify.error;
  return (
    <PortalFrame organisationName={info.data.organisationName}>
      <section className={styles.card} aria-labelledby="portal-sign-in">
        <h1 id="portal-sign-in" className={styles.title}>
          {t('portal.title')}
        </h1>
        <p className={styles.lead}>{t('portal.signIn.heading')}</p>
        {online ? null : <Notice tone="info">{t('errors.offline')}</Notice>}
        {reason === undefined || error !== null ? null : (
          <Notice tone="info">
            {reason === 'expired' ? t('portal.sessionEnded') : t('portal.signedOut')}
          </Notice>
        )}
        {error === null ? null : <Notice tone="error">{errorText(t, language, error)}</Notice>}
        {challenge === null ? (
          <form
            className={styles.form}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              sendCode();
            }}
          >
            <TextField
              label={t('portal.signIn.phone')}
              description={t('portal.signIn.phoneHelp')}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              value={phone}
              onChange={setPhone}
              {...(phoneError === null ? {} : { errorMessage: phoneError })}
            />
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!online}
                isPending={ask.isPending}
                pendingLabel={t('portal.signIn.sending')}
              >
                {t('portal.signIn.sendCode')}
              </Button>
            </div>
          </form>
        ) : (
          <form
            className={styles.form}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              signIn();
            }}
          >
            <Notice tone="info">{t('portal.signIn.codeSent')}</Notice>
            <TextField
              label={t('portal.signIn.code')}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              value={otp}
              onChange={setOtp}
              {...(otpError === null ? {} : { errorMessage: otpError })}
            />
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!online}
                isPending={verify.isPending}
                pendingLabel={t('portal.signIn.pending')}
              >
                {t('portal.signIn.submit')}
              </Button>
              <Button isDisabled={!online || wait > 0 || ask.isPending} onPress={sendCode}>
                {t('portal.signIn.resend')}
              </Button>
              {wait > 0 ? (
                <p className={styles.meta}>
                  {t('portal.signIn.resendIn', { seconds: String(wait) })}
                </p>
              ) : null}
              <Button
                variant="quiet"
                onPress={() => {
                  setChallenge(null);
                  setOtp('');
                  ask.reset();
                  verify.reset();
                }}
              >
                {t('portal.signIn.otherNumber')}
              </Button>
            </div>
          </form>
        )}
        <p className={styles.meta}>{t('portal.signIn.notRegistered')}</p>
      </section>
    </PortalFrame>
  );
}
